import { Center, Flex, Image, Navbar, useMantineTheme } from '@mantine/core';
import { useCallback } from 'react';
import { IconHome, IconMessages } from '@tabler/icons';
import { useUserConditionalRender } from '../../hooks/use-user-conditonal-render';
import { useRenderByPermission } from '../../hooks/use-render-by-permission';
import { UserRole } from '../../models/user';
import { AppRoutePaths, PermissionAlias } from '../../utils/enums';
import { NavbarButton } from '../NavbarButton';
import { CompaniesLink } from './sub-components/companies-link';
import { UsersLink } from './sub-components/users-link';

import { appConfig } from '../../config';
import { rootNavigate } from '../CustomRouter';
import { ActivitiesLink } from './sub-components/activities-link';
import { ApprovalsLink } from './sub-components/approvals-link';
import { BillsLink } from './sub-components/bills-link';
import { FinancialLink } from './sub-components/financial-link';
import { ActivityLayoutLink } from './sub-components/layouts-link';
import { OptionsLink } from './sub-components/options-link';
import { OrderMaterialsLink } from './sub-components/order-materials-link';
import { OrdersLink } from './sub-components/orders-link';
import { PaymentSlipLink } from './sub-components/payment-slip-link';
import { PostalsLink } from './sub-components/postals-link';
import { ProductsLink } from './sub-components/products-link';
import { PurchasesLink } from './sub-components/purchases-link';
import { ReceiptLink } from './sub-components/receipt-link';
import { ReportsLink } from './sub-components/reports-link';
import { SacLink } from './sub-components/sac-link';
import { SegmentationTag } from './sub-components/segmentation-tag-link';
import { TeamsLink } from './sub-components/teams-link';
import { TermDocumentsLink } from './sub-components/term-documents-link';

interface SidebarProps {
  opened: boolean;
  setOpened: React.Dispatch<React.SetStateAction<boolean>>;
}

export function Sidebar({ opened, setOpened }: SidebarProps) {
  const company = process.env.REACT_APP_COMPANY_NAME;
  const theme = useMantineTheme();
  const { isAdminType, isUserTypeWithRoles, isUserTypeWithRole, isMainUser } =
    useUserConditionalRender();
  const { renderByPermission } = useRenderByPermission();

  const navigate = useCallback(
    (to: string) => {
      rootNavigate(to);
      setOpened(false);
    },
    [setOpened],
  );

  return (
    <Navbar
      zIndex={10}
      fixed={false}
      p="md"
      hiddenBreakpoint="md"
      hidden={!opened}
      width={{ lg: 210, md: 200 }}
      style={{ overflowY: 'scroll' }}
    >
      <Navbar.Section grow mt="xs">
        <Center>
          <Image
            height={50}
            width={140}
            fit="contain"
            src={appConfig.sidebarImage}
          />
        </Center>
        <Flex direction="column">
          <NavbarButton
            onClick={() => navigate(AppRoutePaths.HOME)}
            color="gray"
            icon={<IconHome size={16} />}
            text="Home"
            textColor={theme.colorScheme === 'light' ? '#373A40' : '#FBFBFB'}
          />

          {/* Pedidos — visível para todos os papéis regulares */}
          {isUserTypeWithRoles(
            [
              UserRole.COMPANY,
              UserRole.MANAGER,
              UserRole.OTHER,
              UserRole.PACKER,
              UserRole.RECEPTIONIST,
              UserRole.TECHNICAL,
              UserRole.VENDOR,
              UserRole.FINANCIAL,
              UserRole.HANDLER,
              UserRole.SCREEN,
            ],
            <OrdersLink navigate={navigate} themeColor={theme.colorScheme} />,
          )}

          {/* Admin/MASTER: acesso total */}
          {isAdminType(
            <>
              <OrdersLink navigate={navigate} themeColor={theme.colorScheme} />
              <ApprovalsLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <UsersLink navigate={navigate} themeColor={theme.colorScheme} />
              <CompaniesLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <FinancialLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <TeamsLink navigate={navigate} themeColor={theme.colorScheme} />
              <ReportsLink navigate={navigate} themeColor={theme.colorScheme} />
              <OrderMaterialsLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <OptionsLink navigate={navigate} themeColor={theme.colorScheme} />
              <SacLink navigate={navigate} themeColor={theme.colorScheme} />
              <PaymentSlipLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <SegmentationTag
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              {company === 'ltp' && (
                <ProductsLink
                  navigate={navigate}
                  themeColor={theme.colorScheme}
                />
              )}
              <TermDocumentsLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <NavbarButton
                onClick={() => navigate(AppRoutePaths.CHAT_MONITOR)}
                color="blue"
                icon={<IconMessages size={16} />}
                text="Monitorar Chat"
                textColor={
                  theme.colorScheme === 'light' ? '#373A40' : '#FBFBFB'
                }
              />
              {/* 
              <ActivitiesLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <PostalsLink navigate={navigate} themeColor={theme.colorScheme} />
              <ReceiptLink navigate={navigate} themeColor={theme.colorScheme} />
              <ActivityLayoutLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <BillsLink navigate={navigate} themeColor={theme.colorScheme} />
              <PurchasesLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />*/}
            </>,
          )}

          {/* VENDOR */}
          {isUserTypeWithRole(
            UserRole.VENDOR,
            <>
              <ApprovalsLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              {isMainUser(
                <UsersLink
                  navigate={navigate}
                  themeColor={theme.colorScheme}
                />,
              )}
              <CompaniesLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <ActivitiesLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <OrderMaterialsLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <PostalsLink navigate={navigate} themeColor={theme.colorScheme} />
              <ReportsLink navigate={navigate} themeColor={theme.colorScheme} />
              <SacLink navigate={navigate} themeColor={theme.colorScheme} />
              <PaymentSlipLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <SegmentationTag
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <ReceiptLink navigate={navigate} themeColor={theme.colorScheme} />
            </>,
          )}

          {/* RECEPTIONIST */}
          {isUserTypeWithRole(
            UserRole.RECEPTIONIST,
            <>
              <CompaniesLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <OrderMaterialsLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <PostalsLink navigate={navigate} themeColor={theme.colorScheme} />
              <ReceiptLink navigate={navigate} themeColor={theme.colorScheme} />
              <ReportsLink navigate={navigate} themeColor={theme.colorScheme} />
              <PaymentSlipLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
            </>,
          )}

          {/* FINAL_ART */}
          {isUserTypeWithRole(
            UserRole.FINAL_ART,
            <>
              <ActivitiesLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
            </>,
          )}

          {/* COMPANY */}
          {isUserTypeWithRole(
            UserRole.COMPANY,
            <>
              <ActivityLayoutLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <SacLink navigate={navigate} themeColor={theme.colorScheme} />
              <PaymentSlipLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
            </>,
          )}

          {/* FINANCIAL */}
          {isUserTypeWithRole(
            UserRole.FINANCIAL,
            <>
              <ApprovalsLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <FinancialLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <BillsLink navigate={navigate} themeColor={theme.colorScheme} />
              <PurchasesLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <CompaniesLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <ReportsLink navigate={navigate} themeColor={theme.colorScheme} />
              <PostalsLink navigate={navigate} themeColor={theme.colorScheme} />
              <ReceiptLink navigate={navigate} themeColor={theme.colorScheme} />
              <PaymentSlipLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <SacLink navigate={navigate} themeColor={theme.colorScheme} />
            </>,
          )}

          {/* HANDLER */}
          {isUserTypeWithRole(
            UserRole.HANDLER,
            <>
              <ReportsLink navigate={navigate} themeColor={theme.colorScheme} />
            </>,
          )}

          {/* MANAGER */}
          {isUserTypeWithRole(
            UserRole.MANAGER,
            <>
              <ReportsLink navigate={navigate} themeColor={theme.colorScheme} />
            </>,
          )}

          {/* PACKER */}
          {isUserTypeWithRole(
            UserRole.PACKER,
            <>
              <ReportsLink navigate={navigate} themeColor={theme.colorScheme} />
            </>,
          )}

          {/* TECHNICAL */}
          {isUserTypeWithRole(
            UserRole.TECHNICAL,
            <>
              <ReportsLink navigate={navigate} themeColor={theme.colorScheme} />
            </>,
          )}

          {/* SAC */}
          {isUserTypeWithRole(
            UserRole.SAC,
            <>
              <SacLink navigate={navigate} themeColor={theme.colorScheme} />
              <OrdersLink navigate={navigate} themeColor={theme.colorScheme} />
              <CompaniesLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <OrderMaterialsLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
              <PostalsLink navigate={navigate} themeColor={theme.colorScheme} />
            </>,
          )}

          {/* OTHER */}
          {isUserTypeWithRole(
            UserRole.OTHER,
            <>
              <ReportsLink navigate={navigate} themeColor={theme.colorScheme} />
            </>,
          )}

          {/* Itens extras concedidos por permissões individuais (complementar, nunca substitui) */}
          {renderByPermission(
            [PermissionAlias.VIEW_FINANCIAL, PermissionAlias.MANAGE_FINANCIAL],
            () => (
              <FinancialLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
            ),
          )}
          {renderByPermission(
            [
              PermissionAlias.VIEW_ACTIVITIES,
              PermissionAlias.MANAGE_ACTIVITIES,
            ],
            () => (
              <ActivitiesLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
            ),
          )}
          {renderByPermission(
            [PermissionAlias.VIEW_BILLS, PermissionAlias.MANAGE_BILLS],
            () => (
              <BillsLink navigate={navigate} themeColor={theme.colorScheme} />
            ),
          )}
          {renderByPermission(
            [PermissionAlias.VIEW_PURCHASES, PermissionAlias.MANAGE_PURCHASES],
            () => (
              <PurchasesLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
            ),
          )}
          {renderByPermission(
            [PermissionAlias.VIEW_RECEIPTS, PermissionAlias.MANAGE_RECEIPTS],
            () => (
              <ReceiptLink navigate={navigate} themeColor={theme.colorScheme} />
            ),
          )}
          {renderByPermission(
            [PermissionAlias.VIEW_TICKETS, PermissionAlias.MANAGE_TICKETS],
            () => (
              <SacLink navigate={navigate} themeColor={theme.colorScheme} />
            ),
          )}
          {renderByPermission(
            [PermissionAlias.VIEW_POSTALS, PermissionAlias.MANAGE_POSTALS],
            () => (
              <PostalsLink navigate={navigate} themeColor={theme.colorScheme} />
            ),
          )}
          {renderByPermission(
            [
              PermissionAlias.VIEW_PAYMENT_SLIP_GROUPS,
              PermissionAlias.MANAGE_PAYMENT_SLIP_GROUPS,
            ],
            () => (
              <PaymentSlipLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
            ),
          )}
          {renderByPermission(
            [
              PermissionAlias.VIEW_EXPORTABLES,
              PermissionAlias.VIEW_REPORT_ORDERS,
              PermissionAlias.VIEW_REPORT_ACTIVITIES,
              PermissionAlias.VIEW_REPORT_ART,
              PermissionAlias.VIEW_REPORT_PRODUCTION,
              PermissionAlias.VIEW_REPORT_HANDLING,
              PermissionAlias.VIEW_REPORT_PAYMENTS,
              PermissionAlias.VIEW_REPORT_EVENTS,
            ],
            () => (
              <ReportsLink navigate={navigate} themeColor={theme.colorScheme} />
            ),
          )}
          {renderByPermission(
            [
              PermissionAlias.VIEW_ORDER_MATERIAL,
              PermissionAlias.CREATE_ORDER_MATERIAL,
              PermissionAlias.EDIT_ORDER_MATERIAL,
              PermissionAlias.DELETE_ORDER_MATERIAL,
              PermissionAlias.VIEW_MATERIAL_VOLUME_TRANSFER,
              PermissionAlias.HANDLE_MATERIAL_VOLUME_TRANSFER,
            ],
            () => (
              <OrderMaterialsLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
            ),
          )}
          {renderByPermission(
            [PermissionAlias.VIEW_COMPANIES, PermissionAlias.MANAGE_COMPANIES],
            () => (
              <CompaniesLink
                navigate={navigate}
                themeColor={theme.colorScheme}
              />
            ),
          )}
          {renderByPermission([PermissionAlias.ACCESS_CHAT_ADMIN], () => (
            <NavbarButton
              onClick={() => navigate(AppRoutePaths.CHAT_MONITOR)}
              color="blue"
              icon={<IconMessages size={16} />}
              text="Monitorar Chat"
              textColor={theme.colorScheme === 'light' ? '#373A40' : '#FBFBFB'}
            />
          ))}
        </Flex>
      </Navbar.Section>
    </Navbar>
  );
}
